import React from 'react'
import { MenuItem, Select, TableCell, TableRow, TextField, Typography } from '@mui/material'
import * as PXBColors from '@brightlayer-ui/colors';
import { WirelessLumTestGroupFilterDropdownItems } from '../../../enum/ENUM';
import { ORGANIZATTION_NAME } from '../../../../constants';
import { useLanguageLocale } from '../../../../hooks/language-locale-hooks';

type Props = {
    row: any
    onNameChange?: (rowId: number, newName: string) => void
    onZoneChange?: (rowId: number, newZoneId: string | number) => void
    onDescriptionChange?: (rowId: number, newDescription: string) => void
    onTestGroupChange?: (rowId: number, newTestGroupId: number) => void
    hasNameError?: boolean
    hasTestGroupError?: boolean
    hasZoneError?: boolean
}


export const zoneArray = [
    { id: 1, zone: 'Zone 1' },
    { id: 2, zone: 'Zone 2' },
    { id: 3, zone: 'Zone 3' },
    { id: 4, zone: 'Zone 4' },
    { id: 5, zone: 'Zone 5' },
    { id: 6, zone: 'Zone 6' },
    { id: 7, zone: 'Zone 7' },
    { id: 8, zone: 'Zone 8' },
    { id: 9, zone: 'Zone 9' },
    { id: 10, zone: 'Zone 10' },
    { id: 11, zone: 'Zone 11' },
    { id: 12, zone: 'Zone 12' },
    { id: 13, zone: 'Zone 13' },
    { id: 14, zone: 'Zone 14' },
    { id: 15, zone: 'Zone 15' },
    { id: 16, zone: 'Zone 16' },
];

const ConfigureLumnListItems: React.FC<React.PropsWithChildren<React.PropsWithChildren<Props>>> = (props: Props) => {
    const row = props.row;
    const testGroupDropdownItems = WirelessLumTestGroupFilterDropdownItems;
    const { t } = useLanguageLocale();

    return (
        <>
            <TableRow id={`row${row.id}`}
                data-testid={`row${row.id}`}
                style={{ cursor: 'pointer', width: '120%', marginTop: 5, paddingTop: 10, backgroundColor: PXBColors.white[100] }}>
                <TableCell
                    id="config-lumn-favorite"
                    data-testid="config-lumn-favorite">
                </TableCell>
                <TableCell style={{ width: '18%' }}
                    padding='none' data-testid="config-lums-list-zone-name">
                    <Select
                        value={row.zone}
                        onChange={(e) => {
                            props.onZoneChange?.(row.id, e.target.value);
                        }}
                        variant="outlined"
                        disableUnderline
                        fullWidth
                        displayEmpty
                        inputProps={{ 'aria-label': 'Zone' }}
                        MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
                        error={props.hasZoneError} // Show error state
                    >
                        {zoneArray.map((zone) => (
                            <MenuItem key={zone.id} value={zone.id}>
                                {zone.id}. {zone.zone}
                            </MenuItem>
                        ))}
                    </Select>
                    {/*  Add helper text for error */}
                    {props.hasZoneError && (
                        <Typography
                            variant="caption"
                            color="error"
                            sx={{
                                display: 'block',
                                marginTop: '4px',
                                marginLeft: '14px',
                                fontSize: '0.75rem'
                            }}
                        >
                            {t('ASSETS_DETAILS.ZONE_INLINE_ERROR_MESSAGE')}
                        </Typography>
                    )}
                </TableCell>
                <TableCell
                    id="config-lums-list-lumns-name"
                    data-testid="config-lums-list-lumns-name"
                    padding="none"
                    style={{ width: '18%', wordWrap: 'break-word', wordBreak: 'break-all' }}
                >
                    <TextField
                        value={row.name}
                        variant="filled"
                        fullWidth
                        inputProps={{ maxLength: 32, style: { textAlign: 'center' } }}
                        onChange={e => {
                            const newValue = e.target.value;
                            if (newValue === '' || ORGANIZATTION_NAME.test(newValue)) {
                                props.onNameChange?.(row.id, newValue);
                            }
                        }}
                        error={props.hasNameError} //  Show error state
                        helperText={props.hasNameError ? t('ASSETS_DETAILS.LUMINARY_NAME_INLINE_ERROR_MESSAGE') : ''} //  Error message
                    />
                </TableCell>
                <TableCell id="config-lums-list-epas" data-testid="config-lums-list-epas" padding="checkbox" style={{ width: '18%' }}>
                    {row.epasNumber ? row.epasNumber : '-'}
                </TableCell>
                <TableCell id="config-lums-list-product-family" data-testid="config-lums-list-product-family"
                    padding="none" style={{ width: '18%' }}>
                    {row.productFamily ? row.productFamily : '-'}
                </TableCell>
                <TableCell style={{ width: '18%' }} id="config-lums-list-category" data-testid="config-lums-list-category">
                    {row.category ? row.category : '-'}
                </TableCell>
                <TableCell
                    id="config-lums-list-partNo"
                    data-testid="config-lums-list-partNo"
                    style={{ width: '18%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                    {row.lumPartNumber ? row.lumPartNumber : '-'}
                </TableCell>

                <TableCell
                    id="config-lums-list-test-group"
                    data-testid="config-lums-list-test-group"
                    padding="none"
                    style={{ width: '12%' }}
                >
                    <Select
                        value={row.testGroupId}
                        onChange={(e) => {
                            props.onTestGroupChange?.(row.id, Number(e.target.value));
                        }}
                        variant="outlined"
                        disableUnderline
                        fullWidth
                        displayEmpty
                        inputProps={{ 'aria-label': 'Test Group' }}
                        MenuProps={{ PaperProps: { style: { maxHeight: 200 } } }}
                        error={props.hasTestGroupError} // Show error state
                    >
                        {testGroupDropdownItems.map((group) => (
                            <MenuItem key={group} value={group}>
                                {group}
                            </MenuItem>
                        ))}
                    </Select>
                    {/*  Add helper text for error */}
                    {props.hasTestGroupError && (
                        <Typography
                            variant="caption"
                            color="error"
                            sx={{
                                display: 'block',
                                marginTop: '4px',
                                marginLeft: '14px',
                                fontSize: '0.75rem'
                            }}
                        >
                            {t('ASSETS_DETAILS.TEST_GROUP_INLINE_ERROR_MESSAGE')}
                        </Typography>
                    )}
                </TableCell>
                <TableCell
                    id="config-lums-list-rated-duration" data-testid="config-lums-list-rated-duration"
                >
                    {row.ratedDuration ?? '-'}
                </TableCell>
                <TableCell
                    id="config-lums-list-optional-desc"
                    data-testid="config-lums-list-optional-desc"
                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 300 }}
                >
                    <TextField
                        value={row.description}
                        variant="filled"
                        fullWidth
                        inputProps={{ maxLength: 255, style: { textAlign: 'center' } }}
                        onChange={e => {
                            const newValue = e.target.value;
                            if (newValue === '' || ORGANIZATTION_NAME.test(newValue)) {
                                props.onDescriptionChange?.(row.id, newValue);
                            }
                        }}
                    />
                </TableCell>

                {/* This will require later
                 <TableCell id="config-lums-list-icons" data-testid="config-lums-list-product-icons" align="center" padding="none">
                    <Box style={{ display: 'flex', gap: 1, }}>
                        <IconButton id="config-lumn-delete-icon">
                            <DeleteOutlineIcon />
                        </IconButton>
                        <IconButton id="config-lumn-equal-icon">
                            <DensityMediumOutlinedIcon />
                        </IconButton>
                    </Box>
                </TableCell> */}
            </TableRow>
        </>
    )
}

export default ConfigureLumnListItems
